import { z } from "zod";
import { fetchApi } from "../lib/api";
import { parseOrLog } from "@/lib/zod-utils";
import { modulePath } from "./training-services";

// =============================================================================
// Zod Schemas — mirrors training_quizzes.py DTOs
// =============================================================================

export const LearnerQuestionSchema = z.object({
  id: z.string(),
  seq: z.number(),
  prompt: z.string(),
  // The player never sees correct_index — grading is server-side only.
  options: z.array(z.string()),
});
export type LearnerQuestion = z.infer<typeof LearnerQuestionSchema>;

export const AttemptSummarySchema = z.object({
  attempt_count: z.number(),
  best_score: z.number().nullable(),
  passed: z.boolean(),
});

export const LearnerQuizSchema = z.object({
  id: z.string(),
  title: z.string(),
  pass_threshold: z.number(),
  questions: z.array(LearnerQuestionSchema),
  attempts: AttemptSummarySchema,
});
export type LearnerQuiz = z.infer<typeof LearnerQuizSchema>;

export const AttemptResultSchema = z.object({
  score: z.number(),
  passed: z.boolean(),
  pass_threshold: z.number(),
  correct_count: z.number(),
  question_count: z.number(),
  // Per-question correctness keyed by question id — which were wrong,
  // never what the right answer was.
  results: z.record(z.string(), z.boolean()),
});
export type AttemptResult = z.infer<typeof AttemptResultSchema>;

export const AdminQuestionSchema = z.object({
  id: z.string(),
  seq: z.number(),
  prompt: z.string(),
  options: z.array(z.string()),
  correct_index: z.number(),
});

export const AdminQuizSchema = z.object({
  id: z.string(),
  module_id: z.string(),
  title: z.string(),
  pass_threshold: z.number(),
  questions: z.array(AdminQuestionSchema),
  attempt_count: z.number(),
  // Distinct users with a passing attempt — drives the editor's notice that
  // edits never re-test anyone who already passed.
  passed_count: z.number(),
});
export type AdminQuiz = z.infer<typeof AdminQuizSchema>;

/** Editor-side question shape (no server ids — questions are replaced
 *  wholesale on save; order in the array is the saved seq). */
export interface QuestionDraft {
  prompt: string;
  options: string[];
  correct_index: number;
}

// =============================================================================
// API Functions
// =============================================================================

export const getModuleQuiz = async (moduleId: string): Promise<LearnerQuiz> => {
  const raw = await fetchApi(`${modulePath(moduleId)}/quiz`);
  return parseOrLog(LearnerQuizSchema, raw, "training/quiz");
};

export const submitQuizAttempt = async (
  moduleId: string,
  answers: Record<string, number>,
): Promise<AttemptResult> => {
  const raw = await fetchApi(`${modulePath(moduleId)}/quiz/attempts`, {
    method: "POST",
    body: JSON.stringify({ answers }),
  });
  return parseOrLog(AttemptResultSchema, raw, "training/quiz-attempt");
};

const adminQuizPath = (moduleId: string): string =>
  `/training/admin/modules/${moduleId
    .split("/")
    .map((segment) => encodeURIComponent(segment))
    .join("/")}/quiz`;

export const getAdminQuiz = async (moduleId: string): Promise<AdminQuiz> => {
  const raw = await fetchApi(adminQuizPath(moduleId));
  return parseOrLog(AdminQuizSchema, raw, "training/admin-quiz");
};

export const saveAdminQuiz = async (
  moduleId: string,
  body: { title: string; pass_threshold: number; questions: QuestionDraft[] },
): Promise<AdminQuiz> => {
  const raw = await fetchApi(adminQuizPath(moduleId), {
    method: "PUT",
    body: JSON.stringify(body),
  });
  return parseOrLog(AdminQuizSchema, raw, "training/admin-quiz");
};

export const deleteAdminQuiz = async (moduleId: string): Promise<void> => {
  await fetchApi(adminQuizPath(moduleId), { method: "DELETE" });
};
